import { useEffect, useRef, useState } from "react";
import axios from "axios";

export const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_URL });

export const getPlayerToken = () => localStorage.getItem("tk_player_token");
export const setPlayerToken = (t) => localStorage.setItem("tk_player_token", t);
export const getVenueToken = () => localStorage.getItem("tk_venue_token");
export const setVenueToken = (t) => localStorage.setItem("tk_venue_token", t);

export function playerHeaders() {
  const t = getPlayerToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export function venueHeaders() {
  const t = getVenueToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export function formatApiError(e) {
  const detail = e?.response?.data?.detail;
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((x) => x?.msg || JSON.stringify(x)).join(", ");
  return String(detail);
}

export function useGameState(code, asPlayer = false) {
  const [state, setState] = useState(null);
  const offsetRef = useRef(0);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!code) return undefined;
    let alive = true;
    let ws = null;
    let fastPoll = null;
    let lastMsg = 0;

    const absorb = (data) => {
      offsetRef.current = data.server_now - Date.now() / 1000;
      setState(data);
    };

    const poll = async () => {
      try {
        const { data } = await api.get(`/game/${code}/state`, { headers: asPlayer ? playerHeaders() : {} });
        if (!alive) return;
        absorb(data);
      } catch (e) {
        if (!alive) return;
        if (e.response && e.response.status === 404) setState({ notFound: true });
      }
    };

    const startFastPoll = () => {
      if (!fastPoll) fastPoll = setInterval(poll, 1000);
    };

    const connect = () => {
      if (!alive) return;
      try {
        const token = asPlayer ? getPlayerToken() : "";
        const qs = token ? `?token=${encodeURIComponent(token)}` : "";
        ws = new WebSocket(`${API_URL.replace(/^http/, "ws")}/ws/${code}${qs}`);
      } catch (e) {
        startFastPoll();
        return;
      }
      ws.onmessage = (e) => {
        if (!alive) return;
        lastMsg = Date.now();
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "state" && msg.data && msg.data.phase) absorb(msg.data);
        } catch (err) { /* ignore malformed frame */ }
      };
      ws.onclose = (e) => {
        if (!alive) return;
        if (e && e.code === 4404) { setState({ notFound: true }); return; }
        setTimeout(connect, 2500);
      };
      ws.onerror = () => { try { ws.close(); } catch (err) { /* already closing */ } };
    };

    poll();
    connect();
    // Safety net: poll whenever the socket is unhealthy or silent,
    // drop back to a slow 3s poll while live push is working.
    const guard = setInterval(() => {
      const healthy = ws && ws.readyState === 1 && Date.now() - lastMsg < 5000;
      if (!healthy) { poll(); startFastPoll(); }
      else if (fastPoll) { clearInterval(fastPoll); fastPoll = null; }
    }, 3000);
    const tv = setInterval(() => setTick((t) => t + 1), 250);
    return () => {
      alive = false;
      clearInterval(fastPoll);
      clearInterval(guard);
      clearInterval(tv);
      if (ws) { ws.onclose = null; ws.onerror = null; try { ws.close(); } catch (err) { /* already closed */ } }
    };
  }, [code, asPlayer]);

  const secondsLeft =
    state && !state.notFound
      ? Math.max(0, state.phase_ends_at - (Date.now() / 1000 + offsetRef.current))
      : 0;

  return { state, secondsLeft };
}

export const fmtClock = (s) => {
  const v = Math.max(0, Math.ceil(s));
  return `${Math.floor(v / 60)}:${(v % 60).toString().padStart(2, "0")}`;
};

export const pointsIfCorrect = (secondsLeft, qTime = 10, minPts = 100, maxPts = 1000) =>
  Math.round(minPts + (maxPts - minPts) * Math.max(0, Math.min(1, secondsLeft / (qTime || 10))));
